import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Plus, Edit2, Trash2, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';

const TEMPLATES_PADRAO = [
  {
    id: 'pedido-pronto',
    titulo: 'Pedido Pronto',
    descricao: 'Notifica que o pedido está pronto para retirada',
    mensagem: 'Olá {{nome}}! 🎉 Seu pedido #{{codigo}} está pronto para retirada! Aguardamos você em nosso ateliê.',
    variaveis: ['{{nome}}', '{{codigo}}'],
    customizavel: true
  },
  {
    id: 'aguardando-retirada',
    titulo: 'Aguardando Retirada',
    descricao: 'Lembrete que o pedido está aguardando retirada',
    mensagem: 'Oi {{nome}}, tudo bem? 👋 Seu pedido #{{codigo}} está aqui, aguardando sua retirada. Passe quando puder!',
    variaveis: ['{{nome}}', '{{codigo}}'],
    customizavel: true
  },
  {
    id: 'informacoes-pedido',
    titulo: 'Informações do Pedido',
    descricao: 'Envia detalhes do pedido ao cliente',
    mensagem: 'Olá {{nome}}! 📋 Informações do seu pedido #{{codigo}}:\n\nAcompanhe o status e detalhes através do nosso sistema.\n\nQualquer dúvida, é só chamar! 😊',
    variaveis: ['{{nome}}', '{{codigo}}'],
    customizavel: true
  }
];

export function ConfiguracaoWhatsAppTemplates() {
  const [templates, setTemplates] = useState(TEMPLATES_PADRAO);
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [dialogAberto, setDialogAberto] = useState(false);
  const [novoTemplate, setNovoTemplate] = useState({
    titulo: '',
    descricao: '',
    mensagem: ''
  });

  const handleSalvarTemplate = (id: string) => {
    setEditandoId(null);
  };

  const handleResetarPadrao = (id: string) => {
    const templatePadrao = TEMPLATES_PADRAO.find(t => t.id === id);
    if (templatePadrao) {
      setTemplates(templates.map(t =>
        t.id === id ? templatePadrao : t
      ));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Templates de WhatsApp</h1>
        <p className="text-gray-500 mt-1">Personalize as mensagens enviadas aos clientes</p>
      </div>

      {/* Info Box */}
      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="pt-6">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-blue-900">ℹ️ Variáveis disponíveis:</p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{'{{nome}}'}</Badge>
              <Badge variant="secondary">{'{{codigo}}'}</Badge>
              <Badge variant="secondary">{'{{data}}'}</Badge>
              <Badge variant="secondary">{'{{valor}}'}</Badge>
            </div>
            <p className="text-xs text-blue-800 mt-2">Use essas variáveis e serão substituídas automaticamente ao enviar a mensagem.</p>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      <div className="grid gap-4">
        {templates.map((template) => (
          <Card key={template.id}>
            <CardHeader className="flex flex-row items-start justify-between pb-3">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  {template.titulo}
                </CardTitle>
                <CardDescription>{template.descricao}</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleResetarPadrao(template.id)}
                  title="Restaurar padrão"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditandoId(template.id)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-gray-500">Mensagem</Label>
                {editandoId === template.id ? (
                  <Textarea
                    value={template.mensagem}
                    onChange={(e) => {
                      setTemplates(templates.map(t =>
                        t.id === template.id ? { ...t, mensagem: e.target.value } : t
                      ));
                    }}
                    rows={4}
                    className="font-mono text-sm mt-1"
                  />
                ) : (
                  <p className="bg-gray-50 p-3 rounded text-sm whitespace-pre-wrap border border-gray-200">
                    {template.mensagem}
                  </p>
                )}
              </div>

              {editandoId === template.id && (
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditandoId(null)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleSalvarTemplate(template.id)}
                  >
                    Salvar
                  </Button>
                </div>
              )}

              {/* Preview */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
                <p className="text-xs font-semibold text-blue-900 mb-2">Preview com exemplo:</p>
                <p className="text-xs whitespace-pre-wrap">
                  {template.mensagem
                    .replace('{{nome}}', 'João')
                    .replace('{{codigo}}', '001')
                    .replace('{{data}}', '25/12/2026')
                    .replace('{{valor}}', 'R$ 150,00')}
                </p>
              </div>

              <div className="text-xs text-gray-500">
                {template.mensagem.length} caracteres
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info sobre integração */}
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="text-base">Próximas Funcionalidades</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-amber-900">
          <p>✅ Templates personalizáveis (você está aqui)</p>
          <p>⏳ Integração com WhatsApp Business API</p>
          <p>⏳ Automação de envio de mensagens</p>
          <p>⏳ Histórico de mensagens enviadas</p>
        </CardContent>
      </Card>
    </div>
  );
}
